import BaseCard, { ThumbnailProps } from '@/components/ui/Cards/BaseCard';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Fragment } from 'react';
import BaseCardList from '@/components/ui/Cards/BaseCardList';
import moreLikeThis from '@/utils/moreLikeThis';
import { useSelector } from 'react-redux';
import { selectPageLinkPaths } from '@/redux/selectors/pageSelectors';
import { useNavigate } from 'react-router-dom';
import { ItemAnalyticsModel, ItemListAnalyticsModel } from '@/@types/client-api';
import { TDropdownMenuItem } from '../DropdownMenu';
import { trackSelectItem } from '@/analytics/ecommerce';
import { AnalyticsContext } from '@/analytics/constants';

export interface PodcastCardProps {
  image?: {
    url: string;
    alt?: string;
  };
  size?: 'small' | 'medium' | 'large';
  heading?: string;
  labelDetail?: string;
  labelInfo?: string;
  href?: string;
  target?: string;
  onClickCard?: () => void;
  index?: number;
  showIndexOverlay?: boolean;
  heightAuto?: boolean;
  widthAuto?: boolean;
  showAsList?: boolean;
  podcastLinks?: TDropdownMenuItem[];
  productCode?: string | null;
  itemAnalyticsModel?: ItemAnalyticsModel;
  listId?: string;
  listName?: string;
  clickOnCardContent?: () => void;
  itemListAnalyticsModel?: ItemListAnalyticsModel;
  cardTheme?: 'light' | 'default';
  containerClass?: string;
}

export interface PodcastLabelsProps extends PodcastCardProps {
  labelDetailIcon?: string;
  labelDetailClassName?: string;
}

const PodcastCard: React.FC<PodcastCardProps> = ({
  image,
  showAsList,
  podcastLinks,
  productCode,
  itemAnalyticsModel,
  listId,
  listName,
  itemListAnalyticsModel,
  onClickCard,
  cardTheme = 'default',
  ...props
}) => {
  const pageLinkPaths = useSelector(selectPageLinkPaths);
  const searchPageUrl = pageLinkPaths?.Search;
  const navigate = useNavigate();
  const thumbnail: ThumbnailProps = {
    image,
    iconThumbnail: { name: 'play', label: 'Launch now' }
  };

  const handleThumbnailClick = () => {
    trackSelectItem(
      itemAnalyticsModel,
      AnalyticsContext.Cards,
      listId,
      listName,
      itemListAnalyticsModel,
      typeof props.index === 'number' && props.index >= 0 ? props.index : undefined
    );

    if (onClickCard) {
      onClickCard();
    }
    if (props?.href) {
      window.location.href = props.href;
    }
  };

  const handleCardContentClick = () => {
    trackSelectItem(
      itemAnalyticsModel,
      AnalyticsContext.Cards,
      listId,
      listName,
      itemListAnalyticsModel,
      typeof props.index === 'number' && props.index >= 0 ? props.index : undefined
    );
  };

  const buttonOptions: TDropdownMenuItem[] = [
    ...(podcastLinks || []),
    {
      label: 'Show more like this',
      onClick: () => {
        moreLikeThis(navigate, productCode, searchPageUrl, itemAnalyticsModel);
      },
      icon: 'search'
    }
  ];

  let buttonOptionsVariation;

  const belowLg = useMediaQuery(ScreenSizeQueries.belowLg);

  if (belowLg) {
    buttonOptionsVariation = [
      {
        label: 'Launch',
        onClick: () => {
          trackSelectItem(
            itemAnalyticsModel,
            AnalyticsContext.Cards,
            listId,
            listName,
            itemListAnalyticsModel,
            typeof props.index === 'number' && props.index >= 0 ? props.index : undefined
          );
          window.location.href = props?.href ?? '';
        },
        icon: 'play'
      },
      ...(buttonOptions ?? [])
    ];
  } else {
    buttonOptionsVariation = buttonOptions ?? [];
  }

  const podcastProps: PodcastLabelsProps = {
    ...props,
    cardTheme,
    containerClass: cardTheme === 'light' ? 'bg-white' : '',
    onClickCard: handleThumbnailClick,
    clickOnCardContent: handleCardContentClick
  };

  return (
    <Fragment>
      {showAsList ? (
        <BaseCardList
          buttonOptions={buttonOptionsVariation}
          thumbnailProps={thumbnail}
          {...podcastProps}
        />
      ) : (
        <BaseCard
          thumbnailProps={thumbnail}
          buttonOptions={buttonOptionsVariation}
          {...podcastProps}
        />
      )}
    </Fragment>
  );
};

export default PodcastCard;
